// packages/shared/src/utils/error-parser.ts
import {
  ErrorCode,
  type ErrorCodeValue,
  PRESERVE_SERVER_MESSAGE_CODES,
} from '../constants/error-codes';
import {
  ERROR_MESSAGES,
  HTTP_STATUS_MESSAGES,
  TECHNICAL_MESSAGE_PATTERNS,
  type UserFriendlyError,
} from '../constants/error-messages';
import { isApiError } from '../types/api-response';

/**
 * Options for error parsing
 */
export interface ParseErrorOptions {
  fallbackMessage?: string;
  includeDetails?: boolean;
}

/**
 * Check if error is an Axios error
 */
function isAxiosError(error: unknown): error is {
  isAxiosError: true;
  response?: { status: number; data?: unknown };
  code?: string;
  message?: string;
} {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as Record<string, unknown>).isAxiosError === true
  );
}

/**
 * Get error message by error code
 */
export function getErrorByCode(
  code: ErrorCodeValue | string,
  serverMessage?: string
): UserFriendlyError {
  const knownError = ERROR_MESSAGES[code as ErrorCodeValue];

  if (knownError) {
    // For certain errors, use server message if more specific (e.g., validation errors, dynamic messages)
    const useServerMessage =
      serverMessage && PRESERVE_SERVER_MESSAGE_CODES.has(code as ErrorCodeValue);

    if (useServerMessage) {
      return { ...knownError, message: serverMessage };
    }
    return knownError;
  }

  // Unknown error code - use server message if available
  return {
    title: 'Error',
    message: serverMessage || 'An error occurred',
    action: 'Try again',
    actionKey: 'retry',
  };
}

/**
 * Get error message by HTTP status code
 */
export function getErrorByStatus(status: number, serverMessage?: string): UserFriendlyError {
  const knownError = HTTP_STATUS_MESSAGES[status];

  if (knownError) {
    // For 4xx client errors, include server message if available
    if (serverMessage && status >= 400 && status < 500) {
      return { ...knownError, message: serverMessage };
    }
    return knownError;
  }

  // Unknown status code
  if (status >= 500) {
    return ERROR_MESSAGES[ErrorCode.SERVER_ERROR];
  }

  return ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR];
}

/**
 * Check if error is a Prisma error with metadata
 */
interface PrismaError {
  code?: string;
  meta?: {
    target?: string[];
  };
  message?: string;
}

function isPrismaError(error: unknown): error is PrismaError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as Record<string, unknown>).code === 'string'
  );
}

/**
 * Parse Prisma P2002 unique constraint violation
 */
function parsePrismaUniqueConstraintError(error: PrismaError): UserFriendlyError | null {
  if (error.code !== 'P2002') {
    return null;
  }

  // Extract field name from target metadata
  const target = error.meta?.target;
  const field = Array.isArray(target) ? target[0] : null;

  // Map field to appropriate error code
  if (field === 'email') {
    return ERROR_MESSAGES[ErrorCode.USER_EMAIL_EXISTS];
  }

  if (field === 'phone') {
    return ERROR_MESSAGES[ErrorCode.USER_PHONE_EXISTS];
  }

  // Generic unique constraint violation
  return {
    title: 'Duplicate Entry',
    message: 'This value already exists. Please use a different value.',
    action: 'Try again',
    actionKey: 'retry',
  };
}

/**
 * Match technical error message to error code
 */
function matchTechnicalMessage(message: string): UserFriendlyError | null {
  for (const { pattern, errorCode } of TECHNICAL_MESSAGE_PATTERNS) {
    if (pattern.test(message)) {
      return ERROR_MESSAGES[errorCode];
    }
  }
  return null;
}

/**
 * Parse Axios error to user-friendly message
 */
function parseAxiosError(
  error: {
    isAxiosError: true;
    response?: { status: number; data?: unknown };
    code?: string;
    message?: string;
  },
  _options: ParseErrorOptions
): UserFriendlyError {
  // Network error (no response)
  if (error.code === 'ERR_NETWORK' || !error.response) {
    return ERROR_MESSAGES[ErrorCode.NETWORK_ERROR];
  }

  // Timeout error
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return ERROR_MESSAGES[ErrorCode.TIMEOUT_ERROR];
  }

  const { status, data } = error.response;

  // Check for new API envelope format
  if (isApiError(data)) {
    return getErrorByCode(data.error.code, data.error.message);
  }

  // Extract server message from old format
  const responseData = data as Record<string, unknown> | undefined;
  const serverMessage =
    (responseData?.message as string) ||
    ((responseData?.error as Record<string, unknown>)?.message as string);

  // Check if response contains Prisma error
  if (isPrismaError(responseData)) {
    const prismaError = parsePrismaUniqueConstraintError(responseData as PrismaError);
    if (prismaError) {
      return prismaError;
    }
  }

  // Try technical message pattern matching
  if (serverMessage) {
    const matchedError = matchTechnicalMessage(serverMessage);
    if (matchedError) {
      return matchedError;
    }
  }

  // Fall back to HTTP status code
  return getErrorByStatus(status, serverMessage);
}

/**
 * Parse any error into a user-friendly error object
 *
 * Works with:
 * - New API response envelope { success: false, error: { code, message } }
 * - Axios errors with response.data.message
 * - HTTP status codes
 * - Technical error message patterns
 * - Network/timeout errors
 */
export function parseError(error: unknown, options: ParseErrorOptions = {}): UserFriendlyError {
  const { fallbackMessage } = options;

  // 1. Check for new API error response envelope
  if (isApiError(error)) {
    return getErrorByCode(error.error.code, error.error.message);
  }

  // 2. Handle Axios errors
  if (isAxiosError(error)) {
    return parseAxiosError(error, options);
  }

  // 3. Check for Prisma errors (must be before Error instance check)
  if (isPrismaError(error)) {
    const prismaError = parsePrismaUniqueConstraintError(error);
    if (prismaError) {
      return prismaError;
    }
  }

  // 4. Handle Error instances
  if (error instanceof Error) {
    // Check for network errors
    if (error.message.includes('fetch') || error.message.includes('Network')) {
      return ERROR_MESSAGES[ErrorCode.NETWORK_ERROR];
    }

    // Check for timeout
    if (error.message.includes('timeout')) {
      return ERROR_MESSAGES[ErrorCode.TIMEOUT_ERROR];
    }

    // Try pattern matching
    const matchedError = matchTechnicalMessage(error.message);
    if (matchedError) {
      return matchedError;
    }

    // Return with message
    return {
      title: 'Error',
      message: error.message || fallbackMessage || 'An error occurred',
      action: 'Try again',
      actionKey: 'retry',
    };
  }

  // 5. Fallback for unknown errors
  return ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR];
}

/**
 * Extract simple error message string (for backwards compatibility)
 */
export function getErrorMessage(error: unknown): string {
  const parsed = parseError(error);
  return parsed.message;
}

/**
 * Extract error title (for toast title)
 */
export function getErrorTitle(error: unknown): string {
  const parsed = parseError(error);
  return parsed.title;
}
